import {useEffect, useState} from 'react'
import type {IContentItem} from '@kontent-ai/delivery-sdk'
import type {Block} from '@/_new-code/services/kontent-ai/types'

export type CountdownClockContentItem = IContentItem & {
	elements: {
		targetDate?: string
		backgroundImage?: {url: string}[]
		fontColor?: string
		eventName?: string
	}
}

interface TimeLeft {
	days: number
	hours: number
	minutes: number
	seconds: number
}

const convertToNY = (date: Date) =>
	new Date(date.toLocaleString('en-US', {timeZone: 'America/New_York'}))

const parseNYDate = (iso: string) => convertToNY(new Date(iso))
const getNYNow = () => convertToNY(new Date())
const pad = (n: number) => n.toString().padStart(2, '0')

export const CountdownClockBlock: Block<CountdownClockContentItem> = ({
	block,
}) => {
	const rawTarget = (block.elements.targetDate as string) || ''
	const eventName = ((block.elements.eventName as string) || '').trim()
	const bgImage = (
		block.elements.backgroundImage as {url: string}[] | undefined
	)?.[0]?.url
	const fontColor = (block.elements.fontColor as string) || ''
	const isInvalidInput =
		!rawTarget || Number.isNaN(new Date(rawTarget).getTime())
	const isValidInput = !isInvalidInput
	const target = isValidInput ? parseNYDate(rawTarget) : null
	const eventEnd = target ? new Date(target) : null
	if (eventEnd) eventEnd.setHours(23, 59, 59, 999)

	const calculateTimeLeft = (): TimeLeft => {
		if (!target) return {days: 0, hours: 0, minutes: 0, seconds: 0}

		const now = getNYNow()
		const diff = target.getTime() - now.getTime()

		if (diff <= 0) return {days: 0, hours: 0, minutes: 0, seconds: 0}

		return {
			days: Math.floor(diff / (1000 * 60 * 60 * 24)),
			hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
			minutes: Math.floor((diff / (1000 * 60)) % 60),
			seconds: Math.floor((diff / 1000) % 60),
		}
	}

	const [timeLeft, setTimeLeft] = useState<TimeLeft>(calculateTimeLeft())

	useEffect(() => {
		setTimeLeft(calculateTimeLeft())

		const timer = setInterval(() => {
			setTimeLeft(calculateTimeLeft())
		}, 1000)

		return () => {
			clearInterval(timer)
		}
	}, [rawTarget])

	if (isInvalidInput) {
		return (
			<div className="w-full rounded-md bg-red-600 p-4 text-center text-white">
				Invalid or missing target date.
			</div>
		)
	}

	if (eventEnd && getNYNow() > eventEnd) return null

	const isFinished =
		timeLeft.days === 0 &&
		timeLeft.hours === 0 &&
		timeLeft.minutes === 0 &&
		timeLeft.seconds === 0

	if (isFinished && !eventName) return null

	const bgClasses = bgImage
		? `relative flex min-h-[200px] w-full items-center justify-center bg-cover bg-center`
		: `relative flex min-h-[200px] w-full items-center justify-center bg-gradient-to-b from-blue-900 to-cyan-700`

	const fontColorClass = fontColor ? '' : 'text-white'

	return (
		<div
			className={bgClasses}
			style={bgImage ? {backgroundImage: `url(${bgImage})`} : undefined}
		>
			<div className="absolute inset-0 z-10 bg-black/40" />
			<div
				className={`relative z-20 flex flex-col items-center justify-center text-center ${fontColorClass}`}
				style={fontColor ? {color: fontColor} : undefined}
			>
				{isFinished ? (
					<div className="text-[clamp(2rem,6vw,4rem)] font-bold">
						{eventName}
					</div>
				) : (
					<div className="flex flex-wrap items-start justify-center gap-2">
						<div className="m-1 flex min-w-[50px] flex-col items-center">
							<div className="text-[clamp(2rem,8vw,5rem)] font-bold">
								{pad(timeLeft.days)}
							</div>
							<div className="text-[clamp(0.8rem,2vw,1rem)]">
								DAYS
							</div>
						</div>

						<div className="m-1 text-[clamp(2rem,8vw,5rem)] font-bold">
							:
						</div>

						<div className="m-1 flex min-w-[50px] flex-col items-center">
							<div className="text-[clamp(2rem,8vw,5rem)] font-bold">
								{pad(timeLeft.hours)}
							</div>
							<div className="text-[clamp(0.8rem,2vw,1rem)]">
								HOURS
							</div>
						</div>

						<div className="m-1 text-[clamp(2rem,8vw,5rem)] font-bold">
							:
						</div>

						<div className="m-1 flex min-w-[50px] flex-col items-center">
							<div className="text-[clamp(2rem,8vw,5rem)] font-bold">
								{pad(timeLeft.minutes)}
							</div>
							<div className="text-[clamp(0.8rem,2vw,1rem)]">
								MINUTES
							</div>
						</div>

						<div className="m-1 text-[clamp(2rem,8vw,5rem)] font-bold">
							:
						</div>

						<div className="m-1 flex min-w-[50px] flex-col items-center">
							<div className="text-[clamp(2rem,8vw,5rem)] font-bold">
								{pad(timeLeft.seconds)}
							</div>
							<div className="text-[clamp(0.8rem,2vw,1rem)]">
								SECONDS
							</div>
						</div>
					</div>
				)}
			</div>
		</div>
	)
}
